import * as React from 'react';
import type { FileWithPath } from 'react-dropzone';
import { useField } from 'formik';
import styled from 'styled-components';

import { FormikFormGroup, Dropzone, Icon } from 'components/common';
import { Button } from 'components/bootstrap';
import type { EncryptedFieldValue } from 'components/configurationforms/types';

// See https://pki-tutorial.readthedocs.io/en/latest/mime.html#mime-types
const PEM_MIME_TYPES = [
  'application/pkcs8', // .key
  'application/pkix-cert', // .cer
  'application/x-pem-file', // .pem
  'application/x-x509-ca-cert', // .crt
  'application/x-x509-user-cert', // .crt
  'text/plain',
];

const DropzoneInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

type SAMLPEMFieldProps = {
  name: string;
  filename: string;
  label: string;
  help: string;
  maxSize?: number;
  isValueSet: boolean;
  encrypted?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
};

const SAMLPEMField = ({
  name,
  filename,
  label,
  help,
  isValueSet,
  children = undefined,
  encrypted = false,
  maxSize = 1024 * 1024,
  disabled = false,
}: SAMLPEMFieldProps) => {
  const [, , { setValue: setFilename }] = useField<string>(filename);
  const [, { value }, { setValue }] = useField<string | EncryptedFieldValue<any>>(name);

  if (isValueSet) {
    return (
      <FormikFormGroup
        name={filename}
        label={label}
        help={help}
        disabled
        buttonAfter={
          <Button
            type="button"
            bsStyle="danger"
            disabled={disabled}
            onClick={() => {
              if (encrypted && (value as EncryptedFieldValue<any>)?.is_set) {
                setValue({ delete_value: true });
              } else {
                setValue(undefined);
              }
              setFilename(undefined);
            }}>
            Remove
          </Button>
        }
      />
    );
  }

  const handleFileDrop = (files: FileWithPath[]) => {
    const file = files[0];
    const reader = new FileReader();

    reader.addEventListener('load', () => {
      if (encrypted) {
        setValue({ set_value: reader.result.toString() });
      } else {
        setValue(reader.result.toString());
      }
      setFilename(file.name);
    });

    reader.readAsText(file);
  };

  return (
    <FormikFormGroup type="" name={filename} label={label} help={help}>
      <div>
        <Dropzone onDrop={handleFileDrop} accept={PEM_MIME_TYPES} multiple={false} maxSize={maxSize}>
          <DropzoneInner>
            <Dropzone.Accept>
              <Icon name="verified_user" type="regular" size="sm" /> File looks like a PEM file.
            </Dropzone.Accept>
            <Dropzone.Reject>
              <Icon name="error" type="regular" size="sm" /> File type must be one of {PEM_MIME_TYPES.join(', ')}.
            </Dropzone.Reject>
            <Dropzone.Idle>
              <Icon name="verified_user" type="regular" size="sm" />{' '}
              {children || 'Drag a PEM file here or click to select a PEM file'}
            </Dropzone.Idle>
          </DropzoneInner>
        </Dropzone>
      </div>
    </FormikFormGroup>
  );
};

export default SAMLPEMField;
